<script lang="ts">
  import { api } from '../../lib/api';
  import { normalizeSource } from '../../../../shared/src/defaults';
  import type { SourceConfig } from '../../../../shared/src/types';
  import { clone, type Devices, type Overview } from './types';

  let { data, reload }: { data: Overview; reload: () => void } = $props();
  let draft = $state<SourceConfig | null>(null);
  let devices = $state<Devices | null>(null);
  let scanning = $state(false);
  let error = $state<string | null>(null);
  let pipeline = $state<{ id: string; text: string } | null>(null);

  const DECKLINK_MODES = ['auto', '1080i50', '1080i5994', '1080p25', '1080p2997', '1080p30', '1080p50', '1080p5994', '1080p60', '720p50', '720p5994', '720p60', '2160p25', '2160p50'];

  function edit(s: SourceConfig | null) {
    error = null;
    draft = s ? clone(s) : normalizeSource({ name: 'New source', kind: 'test' });
    if (!devices) void scan(false);
  }

  function setKind(kind: SourceConfig['kind']) {
    if (!draft) return;
    draft = normalizeSource({ ...draft, kind, encode: { ...draft.encode, burnIn: kind === 'test' } });
  }

  async function scan(fresh: boolean) {
    scanning = true;
    try {
      devices = await api.get<Devices>(`/api/admin/devices${fresh ? '?refresh=1' : ''}`);
    } finally {
      scanning = false;
    }
  }

  async function save() {
    if (!draft) return;
    try {
      await api.post('/api/admin/sources', draft);
      draft = null;
      reload();
    } catch (err) {
      error = (err as Error).message;
    }
  }

  async function remove(s: SourceConfig) {
    if (!confirm(`Delete source "${s.name}"? Positions using it lose their return feed.`)) return;
    await api.del(`/api/admin/sources/${s.id}`);
    reload();
  }

  async function act(s: SourceConfig, what: 'restart' | 'lose' | 'restore' | 'pipeline') {
    if (what === 'restart') await api.post(`/api/admin/sources/${s.id}/restart`);
    else if (what === 'pipeline') {
      const r = await api.get<{ pipeline: string | null }>(`/api/admin/sources/${s.id}/pipeline`);
      pipeline = { id: s.id, text: (r.pipeline ?? 'not running').replace(/ {2}/g, '\n\n').replace(/ ! /g, ' !\n  ') };
    } else await api.post(`/api/admin/sources/${s.id}/signal`, { present: what === 'restore' });
    reload();
  }
</script>

<div class="panel">
  <div class="row"><h3 class="grow">Sources</h3><button class="primary" onclick={() => edit(null)}>Add source</button></div>
  {#each data.sources as s (s.id)}
    {@const h = data.health[s.id]}
    <div class="list-item">
      <b class="grow">{s.name}</b>
      <span class="badge">{s.kind}{s.kind === 'decklink' ? ` #${s.decklink?.device}` : s.kind === 'ndi' ? `: ${s.ndi?.name}` : ''}</span>
      <span class="muted">{s.encode.width}×{s.encode.height} · {s.encode.bitrateKbps} kbps{s.encode.lowLayer.enabled ? ` + ${s.encode.lowLayer.bitrateKbps}` : ''}</span>
      {#if !s.enabled}<span class="badge">disabled</span>
      {:else if h}<span class="badge {h.signal ? 'ok' : 'bad'}">{h.state}{h.signal ? '' : ' · NO SIGNAL'}</span>{/if}
      <button class="small" onclick={() => edit(s)}>Edit</button>
      <button class="small" onclick={() => act(s, 'restart')}>Restart</button>
      <button class="small" onclick={() => act(s, 'lose')} title="Exercise the NO SIGNAL path">Sim. loss</button>
      <button class="small" onclick={() => act(s, 'restore')}>Restore</button>
      <button class="small" onclick={() => act(s, 'pipeline')}>Pipeline</button>
      <button class="small danger" onclick={() => remove(s)}>Delete</button>
    </div>
  {:else}<p class="muted">No sources yet.</p>{/each}
</div>

<div class="panel" style="margin-top:12px">
  <h3>Browser cameras</h3>
  <p class="muted">Sent from browsers: positions with "Browser camera / video call" enabled, and directors' webcams. Assign them as a return feed (Positions) or play them out as NDI/SDI (Outputs).</p>
  {#each data.browserSources as b (b.id)}
    {@const h = data.health[b.id]}
    <div class="list-item">
      <b class="grow">📷 {b.name}</b>
      {#if b.live}
        <span class="badge ok">LIVE</span>
        <span class="muted">{b.width}×{b.height} · {h?.videoKbps ?? 0} kbps{b.hasAudio ? ` · audio ${h?.audioKbps ?? 0} kbps` : ' · no audio'}</span>
      {:else}<span class="badge">offline</span>{/if}
    </div>
  {:else}<p class="muted">None: enable "Browser camera / video call" on a position.</p>{/each}
</div>

{#if pipeline}
  <div class="panel" style="margin-top:12px">
    <div class="row"><h3 class="grow">GStreamer pipeline</h3><button class="small" onclick={() => (pipeline = null)}>Close</button></div>
    <pre class="mono">{pipeline.text}</pre>
  </div>
{/if}

{#if draft}
  <div class="panel" style="margin-top:12px">
    <h3>{data.sources.some((s) => s.id === draft!.id) ? 'Edit' : 'New'} source</h3>
    <div class="form">
      <label>Name <input bind:value={draft.name} /></label>
      <label>Type
        <select value={draft.kind} onchange={(e) => setKind(e.currentTarget.value as SourceConfig['kind'])}>
          <option value="test">Test pattern</option><option value="decklink">SDI (DeckLink)</option><option value="ndi">NDI</option>
        </select>
      </label>
      <label class="inline"><input type="checkbox" bind:checked={draft.enabled} /> Enabled</label>

      {#if draft.kind === 'test' && draft.test}
        <label>Pattern
          <select bind:value={draft.test.pattern}>
            {#each ['smpte', 'ball', 'bar', 'snow', 'colors', 'smpte100', 'pinwheel', 'gamut'] as p (p)}<option>{p}</option>{/each}
          </select>
        </label>
        <label class="inline"><input type="checkbox" bind:checked={draft.test.tone} /> Tick tone</label>
      {/if}

      {#if draft.kind === 'decklink' && draft.decklink}
        <label>DeckLink device
          <select bind:value={draft.decklink.device}>
            {#each devices?.decklink ?? [] as d (d.device)}<option value={d.device}>{d.device}: {d.name}</option>{/each}
            {#if !devices?.decklink.some((d) => d.device === draft!.decklink!.device)}<option value={draft.decklink.device}>{draft.decklink.device} (not detected)</option>{/if}
          </select>
        </label>
        <label>Connector
          <select bind:value={draft.decklink.connection}><option>sdi</option><option>hdmi</option><option>auto</option></select>
        </label>
        <label>Video mode
          <select bind:value={draft.decklink.mode}>{#each DECKLINK_MODES as m (m)}<option>{m}</option>{/each}</select>
        </label>
        <label>Deinterlace
          <select bind:value={draft.encode.deinterlace}><option value="auto">auto (when interlaced)</option><option value="on">always</option><option value="off">off</option></select>
        </label>
      {/if}

      {#if draft.kind === 'ndi' && draft.ndi}
        <label class="wide">NDI source
          <input list="ndi-sources" bind:value={draft.ndi.name} placeholder="MACHINE (Source name)" />
          <datalist id="ndi-sources">{#each devices?.ndi ?? [] as n (n.name)}<option value={n.name}></option>{/each}</datalist>
        </label>
        <label>Bandwidth <select bind:value={draft.ndi.bandwidth}><option>highest</option><option>lowest</option></select></label>
        <label>Deinterlace
          <select bind:value={draft.encode.deinterlace}><option value="auto">auto</option><option value="on">always</option><option value="off">off</option></select>
        </label>
      {/if}
      {#if draft.kind !== 'test'}
        <div class="wide row">
          <button class="small" disabled={scanning} onclick={() => scan(true)}>{scanning ? 'Scanning…' : 'Rescan devices'}</button>
          <span class="muted">{devices ? `${devices.decklink.length} DeckLink, ${devices.ndi.length} NDI found` : ''}</span>
          {#if devices?.error}<span class="error">{devices.error}</span>{/if}
          {#if draft.kind === 'ndi' && !data.caps.ndi}<span class="error">NDI plugin not installed (scripts/install-ndi.sh)</span>{/if}
          {#if draft.kind === 'decklink' && !data.caps.decklink}<span class="error">DeckLink plugin not available</span>{/if}
        </div>
      {/if}

      <h4 class="wide">Encoding</h4>
      <label>Width <input type="number" bind:value={draft.encode.width} /></label>
      <label>Height <input type="number" bind:value={draft.encode.height} /></label>
      <label>Framerate (0 = source) <input type="number" step="0.01" bind:value={draft.encode.framerate} /></label>
      <label>Bitrate kbps <input type="number" bind:value={draft.encode.bitrateKbps} /></label>
      <label>GOP ms (≤ 1000) <input type="number" max="1000" bind:value={draft.encode.gopMs} /></label>
      <label>Rate control <select bind:value={draft.encode.rateControl}><option value="cbr">CBR</option><option value="vbr">capped VBR</option></select></label>
      <label>Profile <select bind:value={draft.encode.profile}><option value="constrained-baseline">Constrained Baseline</option><option value="main">Main</option></select></label>
      <label>Encoder
        <select bind:value={draft.encode.encoder}>
          <option value="auto">auto</option>
          {#each Object.entries(data.caps.encoders) as [name, e] (name)}<option value={name} disabled={!e.available}>{name}{e.available ? '' : ' (unavailable)'}</option>{/each}
        </select>
      </label>
      <label class="inline"><input type="checkbox" bind:checked={draft.encode.burnIn} /> Latency burn-in</label>
      <label class="inline"><input type="checkbox" bind:checked={draft.encode.lowLayer.enabled} /> Low-bandwidth layer</label>
      {#if draft.encode.lowLayer.enabled}
        <label>Low width <input type="number" bind:value={draft.encode.lowLayer.width} /></label>
        <label>Low height <input type="number" bind:value={draft.encode.lowLayer.height} /></label>
        <label>Low kbps <input type="number" bind:value={draft.encode.lowLayer.bitrateKbps} /></label>
      {/if}

      <h4 class="wide">Audio</h4>
      <label class="inline"><input type="checkbox" bind:checked={draft.audio.enabled} /> Return audio</label>
      {#if draft.kind !== 'test'}
        <label>Input channels <input type="number" bind:value={draft.audio.inputChannels} /></label>
      {/if}
      <label>Channel pair
        <span class="row"><input type="number" min="1" style="width:70px" bind:value={draft.audio.channelPair[0]} /> + <input type="number" min="1" style="width:70px" bind:value={draft.audio.channelPair[1]} /></span>
      </label>
      <label>Opus kbps <input type="number" bind:value={draft.audio.bitrateKbps} /></label>
    </div>
    {#if error}<p class="error">{error}</p>{/if}
    <div class="row" style="margin-top:12px">
      <button class="primary" onclick={save}>Save &amp; apply</button>
      <button onclick={() => (draft = null)}>Cancel</button>
    </div>
  </div>
{/if}

<style>
  pre { white-space: pre-wrap; font-size: 0.78rem; max-height: 420px; overflow: auto; background: #0a0a0a; padding: 12px; border-radius: 8px; }
  h4 { margin: 8px 0 0; }
</style>
